/**
 * Enum value sets for the Edulead fields that can be mapped onto a CRM field.
 *
 * Customers use these lists to configure matching picklists in their CRM: a
 * restricted Salesforce picklist rejects any value outside its value set, so the
 * tokens here are exactly what an export writes - no relabelling, no title case.
 *
 * Sources of truth, all verified against the running system:
 * - type / subtype / access  -> Postgres enums institution_type, institution_subtype, access_type
 * - model                    -> institutions.type_2 (varchar; the ten NCES school models in use)
 * - religiousAffiliation     -> institutions.religious_affiliation (varchar; NCES private school survey values)
 * - decisionLevel / functionalGroup / titleModifier / titleRole / titleArea
 *                            -> @edulead/utils TitleConfig.ts enums
 * - emailVerificationStatus  -> EmailValidationStatus, re-used from the drawer list below
 *
 * Note that account values are SCREAMING_SNAKE tokens while contact values are
 * lowercase with spaces. That difference is real - it is how each field is stored.
 */

import { EMAIL_VALIDATION_STATUSES } from '../custom-components/email-validation-statuses';

export interface EnumFieldInfo {
  title: string;
  description: string;
  /** Caveat shown above the values, e.g. when the set is too large for a picklist. */
  note?: string;
  /** The exported tokens, exactly as a CRM receives them. */
  values: string[];
}

/** Sets too large to model as a restricted picklist. */
const LARGE_SET_NOTE =
  'This set is much larger than a practical picklist. Map it to a text field unless you only care about a few values.';

/**
 * Keyed by the Edulead field name used in CRM field mappings
 * (`CrmMappableField.source`). A field absent from this map is free text.
 */
export const ENUM_FIELD_VALUES: Record<string, EnumFieldInfo> = {
  // ---------------------------------------------------------------- Account
  type: {
    title: 'Type',
    description: 'The primary classification of the institution.',
    values: [
      'DISTRICT', 'SCHOOL', 'AGENCY', 'UNION', 'PARENT_ORG', 'SELPA',
    ],
  },
  subtype: {
    title: 'Subtype',
    description: 'Further classification of the institution type, particularly for districts.',
    values: [
      'REGULAR', 'CHARTER', 'SERVICE', 'SPECIALIZED', 'OTHER', 'SUPERVISORY', 'STATE', 'COMPONENT', 'FEDERAL',
      'SPED', 'VOCATIONAL', 'ALTERNATIVE', 'SELPA',
    ],
  },
  model: {
    title: 'Model',
    description: 'The educational model or special characteristic of the school.',
    values: [
      'ALTERNATIVE', 'CHARTER', 'EARLY_CHILDHOOD_DAY_CARE', 'MILITARY', 'MONTESSORI', 'ONLINE',
      'SPECIAL_EDUCATION', 'SPECIAL_PROGRAM_EMPHASIS', 'VOCATIONAL_TECHNICAL', 'WALDORF',
    ],
  },
  access: {
    title: 'Access',
    description: 'How the institution is funded and who it admits.',
    values: [
      'PUBLIC', 'PRIVATE', 'CONSORTIUM',
    ],
  },
  titleOne: {
    title: 'Title I',
    description:
      'Whether the school receives Title I funding. NCES records only the schools that do, '
      + 'so every other school exports as No rather than blank. Mapped to a checkbox '
      + 'instead of a picklist, the field receives true/false.',
    values: ['Yes', 'No'],
  },
  religiousAffiliation: {
    title: 'Religious Affiliation',
    description: 'The denomination of the institution, populated for private schools. Follows the NCES private school survey.',
    values: [
      'NONSECTARIAN', 'CATHOLIC', 'CHRISTIAN', 'BAPTIST', 'SEVENTH_DAY_ADVENTIST', 'JEWISH',
      'LUTHERAN_CHURCH_MISSOURI_SYNOD', 'EPISCOPAL', 'METHODIST', 'ISLAMIC', 'PRESBYTERIAN', 'PENTECOSTAL',
      'ASSEMBLY_OF_GOD', 'WISCONSIN_EVANGELICAL_LUTHERAN_SYNOD', 'EVANGELICAL_LUTHERAN_CHURCH_IN_AMERICA',
      'CHURCH_OF_CHRIST', 'MENNONITE', 'FRIENDS', 'CALVINIST', 'CHURCH_OF_THE_NAZARENE', 'CHURCH_OF_GOD',
      'OTHER_LUTHERAN', 'BRETHREN', 'GREEK_ORTHODOX', 'AFRICAN_METHODIST_EPISCOPAL', 'AMISH',
      'DISCIPLES_OF_CHRIST', 'CHURCH_OF_GOD_IN_CHRIST', 'LATTER_DAY_SAINTS', 'ANGLICAN', 'OTHER',
    ],
  },

  // ---------------------------------------------------------------- Contact
  decisionLevel: {
    title: 'Decision Level',
    description: 'Where the contact sits in the organization, derived from their title.',
    values: [
      'executive', 'functional leader', 'school leadership', 'operational support', 'practitioner',
    ],
  },
  functionalGroup: {
    title: 'Functional Group',
    description: 'The department the contact works in, derived from their title area and role.',
    values: [
      'curriculum and instruction', 'district administration', 'finance and procurement', 'human resources',
      'operations', 'special education', 'student services', 'technology',
    ],
  },
  emailVerificationStatus: {
    title: 'Email Verification Status',
    description: 'The deliverability result for the contact email address.',
    values: EMAIL_VALIDATION_STATUSES.map(status => status.key),
  },
  titleModifier: {
    title: 'Title Modifier',
    description: 'Prefix that modifies the seniority of a role, parsed out of the raw title.',
    values: [
      'assistant', 'associate', 'administrative', 'chief', 'executive', 'deputy', 'vice', 'senior', 'junior', 'lead',
      'head', 'co', 'adjunct', 'visiting', 'distinguished', 'master', 'managing', 'supervising', 'regional',
      'district', 'alternate', 'founding', 'special',
    ],
  },
  titleRole: {
    title: 'Title Role',
    description: 'The primary job function parsed out of the raw title.',
    note: LARGE_SET_NOTE,
    values: [
      'superintendent', 'director', 'coordinator', 'manager', 'officer', 'specialist', 'supervisor', 'principal',
      'counselor', 'dean', 'analyst', 'administrator', 'program specialist', 'hr generalist', 'assistant',
      'professor', 'teacher', 'paraprofessional', 'member', 'custodian', 'coach', 'instructor', 'president',
      'bus driver', 'nurse', 'librarian', 'tutor', 'paraeducator', 'trainer', 'aide', 'secretary', 'psychologist',
      'staff', 'chair', 'registrar', 'therapist', 'treasurer', 'cook', 'social worker', 'pathologist', 'bookkeeper',
      'faculty', 'developer', 'trustee', 'clerk', 'interventionist', 'receptionist', 'student', 'advisor', 'parent',
      'diagnostician', 'cleaner', 'pastor', 'crossing guard', 'accountant', 'sponsor', 'technician', 'mechanic',
      'educator', 'consultant', 'author', 'groundskeeper', 'moderator', 'interpreter', 'data processor',
      'facilitator', 'engineer', 'recruiter', 'provost', 'chancellor', 'headmaster', 'attorney', 'counsel',
      'clinician', 'physician', 'doctor', 'lecturer', 'liaison', 'mentor', 'advocate', 'fellow', 'strategist',
      'planner', 'navigator', 'generalist', 'monitor', 'representative', 'driver', 'dispatcher', 'attendant',
      'cashier', 'operator', 'inspector', 'guard', 'electrician', 'designer', 'editor', 'architect', 'artist',
      'curator', 'webmaster', 'technologist', 'proctor', 'examiner', 'head', 'associate', 'lead', 'leader',
      'commissioner', 'worker', 'intern', 'minister', 'guide', 'auditor', 'scientist', 'professional', 'translator',
      'helper', 'typist', 'provider', 'founder', 'ceo', 'cfo', 'coo', 'cto', 'cio', 'cmo', 'cao', 'cbo', 'chro',
      'cso', 'cdo', 'clo', 'cco', 'cpo', 'ciso',
    ],
  },
  titleArea: {
    title: 'Title Area',
    description: 'The department, subject or area of responsibility parsed out of the raw title.',
    note: LARGE_SET_NOTE,
    values: [
      'special education', 'technology', 'human resources', 'student services', 'operations',
      'curriculum and instruction', 'finance', 'facilities', 'transportation', 'admissions', 'athletics',
      'development', 'communications', 'special services', 'food services', 'teaching and learning', 'advancement',
      'education', 'maintenance', 'purchasing', 'accounting', 'security', 'enrollment', 'counseling',
      'health services', 'federal programs', 'marketing', 'support services', 'music', 'student support',
      'public information', 'information', 'elementary education', 'secondary education', 'schools',
      'institutional advancement', 'alumni', 'board', 'board of directors', 'cafeteria',
      'diversity equity inclusion', 'band', 'campus', 'academics', 'aquatics', 'special projects', 'department',
      'science', 'social studies', 'speech', 'mathematics', 'spanish', 'language arts', 'custodial', 'history',
      'library', 'business', 'revenue', 'attendance', 'reading', 'physical therapy', 'english', 'biology', 'choir',
      'french', 'chemistry', 'administration', 'title 1', 'educational support', 'art', 'nursing', 'stem', 'cte',
      'compliance', 'mental health', 'exceptional children', 'wellness', 'mtss', 'behavioral health', 'autism',
      'social emotional learning', 'english language learner', 'physical education', 'fine arts', 'world languages',
      'computer science', 'humanities', 'literacy', 'early childhood', 'adult education', 'alternative education',
      'exceptional education', 'bilingual', 'instruction', 'assessment', 'testing', 'accountability', 'data',
      'research', 'innovation', 'safety', 'student affairs', 'student success', 'college counseling',
      'financial aid', 'intervention', 'occupational therapy', 'media', 'payroll', 'administrative services',
      'business services', 'title ix', 'avid', 'special programs', 'accounts payable', 'nutrition', 'kitchen',
      'health', 'guidance', 'curriculum', 'behavior', 'learning', 'performing arts', 'visual arts', 'basketball',
      'football', 'volleyball', 'track', 'baseball', 'softball', 'soccer', 'swimming', 'tennis', 'golf', 'wrestling',
      'cross country', 'boys', 'girls', 'engineering', 'instructional technology', 'edtech', 'digital learning',
      'learning technology', 'audit', 'budget', 'campus ministry', 'religious education', 'faith formation',
      'catechesis', 'youth ministry', 'childrens ministry', 'worship', 'liturgy', 'missions', 'discipleship',
      'evangelization', 'pastoral care', 'spiritual life', 'spiritual formation', 'congregational care',
      'annual giving', 'major gifts', 'planned giving', 'gift planning', 'donor relations', 'donor engagement',
      'donor development', 'stewardship', 'grants', 'grants management', 'grants administration', 'philanthropy',
      'fundraising', 'foundation relations', 'corporate relations', 'institutional giving', 'community engagement',
      'community outreach', 'community relations', 'outreach', 'parent engagement', 'parent relations',
      'family engagement', 'family relations', 'family services', 'partnerships', 'strategic partnerships',
      'community partnerships', 'volunteer services', 'constituent relations', 'constituent engagement',
      'government relations', 'legislative affairs', 'intergovernmental affairs', 'student life', 'campus life',
      'residential life', 'student activities', 'co-curriculars', 'summer programs', 'summer camp', 'summer school',
      'after school', 'aftercare', 'extended day', 'extended care', 'child care', 'childcare', 'daycare',
      'child development', 'international programs', 'international students', 'global programs', 'gifted education',
      'advanced academics', 'college readiness', 'college access', 'college success', 'career readiness',
      'career pathways', 'postsecondary success', 'career services', 'career center', 'career development',
      'college placement', 'workforce development', 'workforce training', 'talent', 'talent management',
      'employee relations', 'benefits', 'total rewards', 'compensation', 'recruitment', 'professional development',
      'professional learning', 'staff development', 'grounds', 'buildings and grounds', 'construction',
      'new construction', 'capital projects', 'capital programs', 'fleet services', 'fleet maintenance',
      'environmental services', 'warehouse', 'risk management', 'insurance', 'emergency management', 'utilities',
      'energy', 'academic support', 'academic success', 'academic achievement', 'academic excellence', 'tutoring',
      'transition services', 'disability services', 'disability resources', 'student accessibility',
      'related services', 'specialized services', 'specialized learning', 'public relations', 'publicity',
      'public affairs', 'external relations', 'external affairs', 'creative services', 'graphic design',
      'video production', 'clinical services', 'medical services', 'therapy services', 'social work', 'theater',
      'dance', 'arts education', 'strategic planning', 'strategic initiatives', 'strategic projects',
      'strategic programs', 'policy', 'policy services', 'public policy', 'education policy', 'school improvement',
      'school innovation', 'educator development', 'leadership development', 'organizational development',
      'event services', 'conference services', 'veteran services', 'sustainability', 'outdoor education',
      'legal services', '21st century', 'dyslexia', 'agriculture', 'plant science', 'animal science',
      'natural resources', 'ffa', 'hospitality', 'tourism',
    ],
  },
};

/** The value set for a mappable field, or undefined when the field is free text. */
export function getEnumFieldInfo(source: string): EnumFieldInfo | undefined {
  return ENUM_FIELD_VALUES[source];
}
